import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, unref as _unref, createBlock as _createBlock, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderList as _renderList, mergeProps as _mergeProps, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-422e3bb2"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "product-card__loading"
}
const _hoisted_2 = {
  key: 0,
  class: "product-card__header"
}
const _hoisted_3 = { class: "product-card__title" }
const _hoisted_4 = { class: "product-card__fetching" }
const _hoisted_5 = { class: "product-card__body product-card__items" }
const _hoisted_6 = { class: "product-card__items-row-data-wrapper" }
const _hoisted_7 = { class: "product-card__items-row" }
const _hoisted_8 = {
  key: 0,
  class: "product-card__items-price"
}
const _hoisted_9 = { class: "product-card__items-price-value" }
const _hoisted_10 = { class: "product-card__items-price-type" }
const _hoisted_11 = { class: "product-card__quantity-wrapper" }
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("hr", null, null, -1))
const _hoisted_13 = { class: "product-card__footer" }
const _hoisted_14 = { class: "product-card__total" }
const _hoisted_15 = {
  key: 0,
  class: "product-card__total-price"
}
const _hoisted_16 = {
  key: 0,
  class: "product-card__label"
}
const _hoisted_17 = { class: "product-card__prices-wrapper" }
const _hoisted_18 = {
  key: 0,
  class: "product-card__price product-card__price--base product-card__price--discounted"
}
const _hoisted_19 = { key: 1 }
const _hoisted_20 = { class: "product-card__action" }
const _hoisted_21 = {
  key: 1,
  class: "product-card__alerts"
}

import { computed, PropType, ref, watch } from 'vue'
import { useFetchServiceProductInformationMutation } from '@/queries/public/services'
import { formatCurrency } from '@/utils/formatters'
import BaseNumberIncrementer from '../BaseNumberIncrementer.vue'
import { ALERT_TYPES, FORMATS, SIZES } from '@/enums/components'
import BaseButton from '../BaseButton.vue'
import { ProductInformation } from '@/models/public/service.model'
import { getValidationErrorData } from '@/utils/errors'
import { Alert } from '@/models/shared.model'
import {
  createOrUpdateProductOrderItem,
  useCreateOrderMutation,
} from '@/queries/cart'
import {
  CreateProductServiceOrderItem,
  OrderItem,
} from '@/models/public/order.model'
import { useOrderStore } from '@/store/order'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import { isNumeric } from '@/utils/general'
import { debounce } from 'lodash'
import { serviceResultCardCommonProps, TRANSLATION_TYPE } from '@/enums/public'
import BaseIcon from '@/components/BaseIcon.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductResultCard',
  props: {
  ...serviceResultCardCommonProps,
  selectedDate: {
    type: Date as PropType<Date | null>,
    default: null,
  },
  selectedGroupSize: {
    type: Number as PropType<number | undefined>,
    default: undefined,
  },
},
  emits: ['cartUpdated', 'payloadUpdated'],
  setup(__props, { emit: emits }) {

const props = __props

const orderStore = useOrderStore()
const i18n = useI18n()
const toast = useToast()



const serviceInfo = ref<ProductInformation | undefined>(undefined)
const isLoadedOnce = ref<boolean>(false)
const isAddToCartLoading = ref<boolean>(false)
const errors = ref<Record<string, unknown> | undefined>(undefined)

const quantity = ref<number>(props.selectedGroupSize || 0)

const isContentSelected = computed<boolean>(() => {
  return quantity.value !== 0
})

const payload = computed<CreateProductServiceOrderItem>(() => {
  return {
    service: props.service.id,
    quantity: quantity.value,
    referral_type: props.referral?.referral_type,
    referral_code: props.referral?.referral_code,
    item_group: null,
    for_date: props.selectedDate,
  }
})

/**
 * Total base price without discounts. For final value use formattedTotalPrice
 */
const formattedBaseTotalPrice = computed<string>(() => {
  const baseTotal = serviceInfo.value?.base_total
  return isNumeric(baseTotal) ? formatCurrency(baseTotal) : '- €'
})

/**
 * Overall total price with discounts included
 */
const formattedTotalPrice = computed<string>(() => {
  const discountedTotal = serviceInfo.value?.discounted_total
  return isNumeric(discountedTotal) ? formatCurrency(discountedTotal) : '- €'
})

const alerts = computed<Alert[]>(() => {
  const alertList: Alert[] = []
  const nonFieldErrors = (errors.value?.non_field_errors || []) as Array<string>
  for (const errorMessage of nonFieldErrors) {
    alertList.push({
      text: errorMessage,
      alertType: ALERT_TYPES.DANGER,
    })
  }
  return alertList
})

const { mutateAsync: fetchServiceInfo, isLoading: isLoadingServiceInfo } =
  useFetchServiceProductInformationMutation(
    ref(props.isPreview ? props.service.id : props.service.hash_id),
    props.isPreview,
  )

const updateServiceInfo = () => {
  fetchServiceInfo({
    quantity: quantity.value,
    group_service: props.groupService,
  })
    .then((data) => {
      serviceInfo.value = data
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
    .finally(() => {
      isLoadedOnce.value = true
    })
}

// Use this to get updates in controlled amount after group size changes.
const debouncedUpdateServiceInfo = debounce(updateServiceInfo, 400)

const { mutateAsync: createOrder } = useCreateOrderMutation()

const addItemToCart = async () => {
  isAddToCartLoading.value = true
  errors.value = undefined
  if (!orderStore.orderUuid) {
    try {
      const data = await createOrder()
      orderStore.updateOrder(data)
    } catch (error: unknown) {
      errors.value = getValidationErrorData(error)
      isAddToCartLoading.value = false
      return
    }
  }

  createOrUpdateProductOrderItem(orderStore.orderUuid, payload.value)
    .then((response) => {
      orderStore.updateOrder(response.data)
      toast.success(i18n.t('addToCartSuccess'))
      emits('cartUpdated')
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
    .finally(() => {
      isAddToCartLoading.value = false
    })
}

const isDiscounted = computed<boolean>(() => {
  return serviceInfo.value?.base_total !== serviceInfo.value?.discounted_total
})

const matchingItemInCart = computed<OrderItem | undefined>(() => {
  for (const item of orderStore.items) {
    // Shouldn't match against order item that is part of a group
    if (item.item_group !== null) {
      continue
    }
    if (item.service === props.service.id) {
      return item
    }
  }
  return undefined
})

// Get initial data right away
updateServiceInfo()

// When group size or additional services quantity changes, ask for updated information
watch(quantity, () => {
  debouncedUpdateServiceInfo()
})

watch(
  () => props.selectedGroupSize,
  () => {
    quantity.value = props.selectedGroupSize || 0
  },
)

watch(
  matchingItemInCart,
  () => {
    if (!matchingItemInCart.value) {
      return
    }
    quantity.value = matchingItemInCart.value.quantity
  },
  { immediate: true },
)

watch(
  payload,
  () => {
    emits('payloadUpdated', payload.value)
  },
  { immediate: true },
)



return (_ctx: any,_cache: any) => {
  const _component_LoadingSpinner = _resolveComponent("LoadingSpinner")!
  const _component_AlertBanner = _resolveComponent("AlertBanner")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["product-card", { 'result-card--grouped': !!props.groupService }])
  }, [
    (!isLoadedOnce.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_component_LoadingSpinner)
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _renderSlot(_ctx.$slots, "header", {}, () => [
            (!_ctx.hideHeader)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createTextVNode(_toDisplayString(_ctx.service.name?.content) + " ", 1),
                    (_ctx.service.name?.type === _unref(TRANSLATION_TYPE).MACHINE)
                      ? (_openBlock(), _createBlock(BaseIcon, {
                          key: 0,
                          icon: "google-translate",
                          style: {"height":"16px","width":"revert","display":"block"}
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _withDirectives(_createElementVNode("div", _hoisted_4, [
                    _createVNode(_component_LoadingSpinner)
                  ], 512), [
                    [_vShow, _unref(isLoadingServiceInfo)]
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          (!_ctx.showSelectedMode)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      (!_ctx.isPriceHidden)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(formatCurrency)(serviceInfo.value?.price)), 1),
                            _createElementVNode("span", _hoisted_10, _toDisplayString('/' + String(serviceInfo.value?.unit_label, '')), 1)
                          ]))
                        : _createCommentVNode("", true),
                      _createVNode(BaseNumberIncrementer, {
                        amount: quantity.value,
                        "min-amount": serviceInfo.value?.min_amount || 1,
                        "max-amount": serviceInfo.value?.max_amount || null,
                        class: "product-card__incrementer-field",
                        onClickMinus: _cache[0] || (_cache[0] = () => (quantity.value = Math.max(0, quantity.value - 1))),
                        onClickPlus: _cache[1] || (_cache[1] = () => (quantity.value = Math.max(0, quantity.value + 1)))
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_11, _toDisplayString(quantity.value), 1)
                        ]),
                        _: 1
                      }, 8, ["amount", "min-amount", "max-amount"])
                    ]),
                    (errors.value?.sales_items?.[_ctx.salesItem.identifier])
                      ? (_openBlock(), _createBlock(_component_AlertBanner, {
                          key: 0,
                          "alert-type": _unref(ALERT_TYPES).WARNING,
                          text: errors.value?.sales_items?.[_ctx.salesItem.identifier]
                        }, null, 8, ["alert-type", "text"]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _hoisted_12
              ], 64))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, [
              (!_ctx.isPriceHidden)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    (!_ctx.showSelectedMode)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(_ctx.$t('totalPrice')), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_17, [
                      (_unref(isDiscounted))
                        ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(_unref(formattedBaseTotalPrice)), 1))
                        : _createCommentVNode("", true),
                      _createElementVNode("span", {
                        class: _normalizeClass(["product-card__price", [_unref(isDiscounted) && 'product-card__price--discounted']])
                      }, _toDisplayString(_unref(formattedTotalPrice)), 3)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (_ctx.showSelectedMode)
                ? (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(quantity.value) + " " + _toDisplayString(String(serviceInfo.value?.unit_label, '')), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_20, [
              _renderSlot(_ctx.$slots, "addButton", {
                payload: _unref(payload),
                contentSelected: _unref(isContentSelected)
              }, () => [
                _createVNode(BaseButton, {
                  format: _unref(FORMATS).PRIMARY,
                  size: _unref(SIZES).SMALL,
                  label: _unref(matchingItemInCart) ? _ctx.$t('updateInCart') : _ctx.$t('addToCart'),
                  loading: isAddToCartLoading.value,
                  class: _normalizeClass(["product-card__action", 
                _unref(matchingItemInCart)
                  ? 'product-card__action--update'
                  : 'product-card__action--add'
              ]),
                  icon: "shopping-cart",
                  disabled: _ctx.isPreview || !_ctx.service.id || quantity.value == 0,
                  "icon-right": "",
                  onClick: _withModifiers(addItemToCart, ["prevent"])
                }, null, 8, ["format", "size", "label", "loading", "class", "disabled", "onClick"])
              ])
            ])
          ]),
          (_unref(alerts).length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(alerts), (alert, alertIndex) => {
                  return (_openBlock(), _createBlock(_component_AlertBanner, _mergeProps({ key: alertIndex }, alert, { class: "product-card__alert" }), null, 16))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ], 64))
  ], 2))
}
}

})